import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import HSTSCustomStyle from "../../utils/hstsCustomStyle";
import DataTable from "react-data-table-component";
import { TopHeader } from "./inc/topHeader";
import { SideBar } from "./inc/sideBar";
import {
  getAllCoordinators,
  updateCoordinatorStatus,
} from "../../services/CoordinatorService";
import { HSTSToast } from "../../utils/hstsToast";

const ManageCoordinator = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState(null);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  const navigator = useNavigate();
  const [coordinators, setCoordinators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [perPage, setPerPage] = useState(5);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (windowWidth < 768) {
      setSidebarOpen(false);
    }
  }, [windowWidth]);

  const toggleMenu = (menu) => {
    setActiveMenu(activeMenu === menu ? null : menu);
  };

  const fetchAllCoordinators = () => {
    setLoading(true);
    getAllCoordinators()
      .then((response) => {
        setCoordinators(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching coordinators:", error);
        setError("Failed to fetch coordinators data");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAllCoordinators();
  }, []);

  const viewMore = (id) => navigator(`/admin/coordinatorDetails/${id}`);

  const authHandler = async (coordinatorId) => {
    const coordinator = coordinators.find(
      (c) => c.coordinatorId === coordinatorId
    );
    if (!coordinator) return;

    const currentStatus = coordinator.coordinatorStatus?.toLowerCase();
    const newStatus = currentStatus === "enable" ? "disable" : "enable";
    const actionText = newStatus === "enable" ? "Enable" : "Disable";

    const confirmAction = window.confirm(
      `Are you sure you want to ${actionText} this coordinator?`
    );

    if (!confirmAction) {
      alert("Action cancelled.");
      return;
    }

    try {
      await updateCoordinatorStatus(coordinatorId, newStatus);

      setCoordinators((prev) =>
        prev.map((c) =>
          c.coordinatorId === coordinatorId
            ? { ...c, coordinatorStatus: newStatus }
            : c
        )
      );

      HSTSToast("success", `Coordinator has been successfully ${actionText}d.`);
    } catch (error) {
      console.error("Error updating coordinator status:", error);
      HSTSToast(
        "error",
        "Failed to update coordinator status. Please try again."
      );
    }
  };

  const filteredCoordinators = coordinators.filter((coordinator) => {
    const searchTerm = searchText.toLowerCase();
    return (
      coordinator.fullName?.toLowerCase().includes(searchTerm) ||
      coordinator.phoneNumber?.toLowerCase().includes(searchTerm) ||
      coordinator.emailAddress?.toLowerCase().includes(searchTerm) ||
      coordinator.position?.toLowerCase().includes(searchTerm) ||
      coordinator.gender?.toLowerCase().includes(searchTerm)
    );
  });

  const columns = [
    {
      name: "S/N",
      selector: (row, index) => index + 1,
      width: "70px",
    },
    {
      name: "Coordinator Name",
      selector: (row) =>
        row.fullName
          ?.split(" ")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" "),
    },
    {
      name: "Phone Number",
      selector: (row) => row.phoneNumber,
      width: "160px",
    },
    {
      name: "Email",
      selector: (row) => row.emailAddress,
    },
    {
      name: "Gender",
      selector: (row) => row.gender,
      width: "120px",
    },
    {
      name: "Status",
      width: "120px",
      cell: (row) => {
        const status = row.coordinatorStatus?.toLowerCase();
        let displayText = row.coordinatorStatus || "N/A";
        let badgeClass = "bg-secondary text-white";

        if (status === "enable") {
          displayText = "Active";
          badgeClass = "bg-success text-white";
        } else if (status === "disable") {
          displayText = "In Active";
          badgeClass = "bg-danger text-white";
        }
        return (
          <div className="text-center justify-content-center align-items-center">
            <span className={`badge fw-bold fs-6 ${badgeClass}`}>
              {displayText}
            </span>
          </div>
        );
      },
    },
    {
      name: "View",
      cell: (row) => (
        <button
          className="btn btn-link text-center fs-4 text-primary zis-ponter"
          title="Manage coordinator"
          onClick={() => viewMore(row.coordinatorId)}
        >
          <i className="fas fa-user-cog"></i>
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "80px",
    },
    {
      name: "Action",
      cell: (row) => {
        const isEnabled = row.coordinatorStatus?.toLowerCase() === "enable";
        return (
          <button
            className={`btn btn-link text-center fs-4 ${
              isEnabled ? "text-danger" : "text-success"
            } zis-ponter`}
            title={isEnabled ? "Disable coordinator" : "Enable coordinator"}
            onClick={() => authHandler(row.coordinatorId)}
          >
            <i
              className={`fas ${isEnabled ? "fa-user-times" : "fa-user-check"}`}
            ></i>
          </button>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "80px",
    },
  ];

  return (
    <>
      {/* <div className="wrapper"> */}
      <div className="d-flex position-relative" style={{ minHeight: "100vh" }}>
        <SideBar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          activeMenu={activeMenu}
          toggleMenu={toggleMenu}
          windowWidth={windowWidth}
        />
        <div className="main-panel">
          <TopHeader
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            windowWidth={windowWidth}
          />

          <div className="container mt-0">
            <div className="page-inner">
              <div className="page-header bg-white zis-shadow rounded-2 mt-2 ">
                <ul className="breadcrumbs my-md-3 mb-3  ms-0">
                  <li className="nav-home">
                    <Link to="/admin" className="ps-3 ps-md-0">
                      <span className="fa fa-home"></span>
                    </Link>
                  </li>
                  <li className="separator">
                    <i className="fas fa-chevron-right text-secondary"></i>
                  </li>
                  <li className="nav-item text-black fw-semibold">
                    <Link to="#">Manage Coordinators</Link>
                  </li>
                </ul>
              </div>

              <div className="row">
                <div className="col-md-12">
                  <div className="card zis-shadow">
                    <div className="card-header">
                      <div className="card-title d-flex mt-3">
                        <h3 className="mt-2">Manage Coordinators</h3>
                        <button
                          className="ms-auto d-none d-sm-inline btn btn-black rounded-5 py-0"
                          onClick={() => navigator("/admin/addCoordinator")}
                        >
                          {" "}
                          <i className="fa fa-plus-circle me-1"></i> Add
                          coordinator
                        </button>
                      </div>
                    </div>
                    <div className="card-header d-sm-none">
                      <div className="d-flex">
                        <div className="d-flex align-items-center">
                          <span className="me-2">Show</span>
                          <select
                            className="form-select form-select-sm"
                            style={{ width: "80px" }}
                            value={perPage}
                            onChange={(e) => setPerPage(Number(e.target.value))}
                          >
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                          </select>
                          <span className="ms-2">entries</span>
                        </div>
                        <div className="ms-auto">
                          <button
                            className="ms-auto btn btn-black rounded-5 py-2"
                            onClick={() => navigator("/admin/addCoordinator")}
                          >
                            {" "}
                            <i className="fa fa-plus-circle me-1"></i> Add
                            coordinator
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="card-header d-flex justify-content-between align-items-center pt-4 px-4">
                      <div className="d-none d-sm-flex align-items-center">
                        <span className="me-2">Show</span>
                        <select
                          className="form-select form-select-sm"
                          style={{ width: "80px" }}
                          value={perPage}
                          onChange={(e) => setPerPage(Number(e.target.value))}
                        >
                          <option value="5">5</option>
                          <option value="10">10</option>
                          <option value="20">20</option>
                          <option value="50">50</option>
                        </select>
                        <span className="ms-2">entries</span>
                      </div>
                      <div className="d-flex ms-auto">
                        <input
                          type="text"
                          className="form-control form-control-sm me-2"
                          placeholder="Search..."
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          style={{ width: "200px" }}
                        />
                      </div>
                    </div>

                    <div className="card-body">
                      <div className="table-responsive">
                        {error ? (
                          <div className="text-center text-danger py-3">
                            {error}
                          </div>
                        ) : (
                          <DataTable
                            key={`data-table-${perPage}`}
                            columns={columns}
                            data={filteredCoordinators}
                            progressPending={loading}
                            progressComponent={
                              <div className="text-center my-3">
                                Loading coordinators...
                              </div>
                            }
                            noDataComponent={
                              <div className="text-center py-3">
                                No coordinators available
                              </div>
                            }
                            pagination
                            paginationPerPage={perPage}
                            paginationRowsPerPageOptions={[5, 10, 20, 50]}
                            customStyles={HSTSCustomStyle}
                            highlightOnHover
                            responsive
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageCoordinator;
